import { DocumentEntity } from "../../domain/entities/Document";
import { STUCK_JOB_MINUTES } from "../../data/api/statementReviewApi";

/**
 * Row state for a PDF statement on the Reducto review-before-push flow.
 *
 * Derived from the newest job_queue row the documents list now returns
 * alongside each statement, so the list, the badge and the action buttons all
 * agree without a second round-trip per row.
 *
 *   extracting  — queued or running; nothing for the user to do yet
 *   ready       — extraction finished, waiting on the review gate
 *   failed      — the extraction failed; offer a manual Retry
 *   stuck       — still queued/running well past the expected 1–4 minutes,
 *                 so the user gets a Retry rather than an indefinite spinner
 *   none        — not a Reducto statement (legacy Textract path, or an
 *                 already-completed statement)
 */
export type ReductoRowState =
  | "extracting"
  | "ready"
  | "failed"
  | "stuck"
  | "none";

const minutesSince = (timestamp?: string | null): number | null => {
  if (!timestamp) return null;
  // MySQL hands back "YYYY-MM-DD HH:MM:SS" (server-local). Safari refuses
  // that form, so normalise to the ISO shape before parsing.
  const parsed = Date.parse(timestamp.replace(" ", "T"));
  if (Number.isNaN(parsed)) return null;
  return (Date.now() - parsed) / 60000;
};

export const getReductoRowState = (doc: DocumentEntity): ReductoRowState => {
  if (doc.type !== "statement") return "none";

  if (doc.status === "awaiting_approval") return "ready";

  if (doc.jobType !== "reducto_extraction") return "none";

  if (doc.jobStatus === "failed" || doc.textractStatus === "failed") {
    return "failed";
  }

  if (doc.jobStatus === "pending" || doc.jobStatus === "processing") {
    // Prefer the server-computed age: the raw timestamps are server-local,
    // so parsing them against the browser's clock breaks the moment the
    // viewer's timezone differs from the server's.
    const age =
      typeof doc.jobAgeMinutes === "number"
        ? doc.jobAgeMinutes
        : minutesSince(doc.jobUpdatedAt || doc.jobCreatedAt);
    if (age !== null && age >= STUCK_JOB_MINUTES) return "stuck";
    return "extracting";
  }

  return "none";
};

export const REDUCTO_STATE_LABEL: Record<ReductoRowState, string> = {
  extracting: "Processing statement",
  // ready: no text on purpose — the Review button says it all
  ready: "",
  failed: "Failed",
  stuck: "Taking longer than usual",
  none: "",
};

/** A manual Retry is offered for both real failures and stuck jobs. */
export const canRetryReducto = (state: ReductoRowState): boolean =>
  state === "failed" || state === "stuck";
